import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { BadgeCheck, CircleX, NotebookPen } from 'lucide-react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';

import { Image } from '@/components/custom-ui/Image';
import { createIDColumn } from '@/components/custom-ui/TableColumns/IDColumn';
import { StyledCellColumn } from '@/components/custom-ui/TableColumns/StyledCell';
import AvatarToolTip from '@/UI/AvatarToolTip';
import { DataTable } from '@/UI/TableShadcn';
import { cn } from 'lib/utils';

import type { ITodayOffersForRequest } from '../../PurchasesRequests/api/Request.interface';
import { GetTodayOffersForRequestApi } from '../api/OffersRequest.api';

import ActionCol from './AllRequests/ActionCol';
import { ClientClassTypography } from './AllRequests/ClassCol';
import ClientCol from './AllRequests/ClientCol';

type Props = {
  OneOfferSelectForPrepareFn: (newValue: ITodayOffersForRequest) => void;
};

// Memoize the decorative elements to prevent re-rendering
const DecorativePatterns = memo(() => (
  <>
    {/* Pattern overlay - Dots pattern for both dark and light modes */}
    <div
      className="absolute inset-0 opacity-30 dark:opacity-50"
      style={{
        backgroundImage:
          'radial-gradient(rgba(0, 0, 0, 0.4) 1px, transparent 1px)',
        backgroundSize: '15px 15px',
      }}
    />

    {/* Additional dots pattern with different size */}
    <div
      className="absolute inset-0 opacity-20 dark:opacity-40"
      style={{
        backgroundImage:
          'radial-gradient(rgba(99, 102, 241, 0.5) 1px, transparent 1px)',
        backgroundSize: '30px 30px',
        backgroundPosition: '7px 7px',
      }}
    />

    {/* Third layer of dots for more depth */}
    <div
      className="absolute inset-0 opacity-15 dark:opacity-35"
      style={{
        backgroundImage:
          'radial-gradient(rgba(0, 0, 0, 0.3) 1px, transparent 1px)',
        backgroundSize: '45px 45px',
        backgroundPosition: '15px 15px',
      }}
    />

    {/* Accent gradients - more subtle in light mode, more vibrant in dark */}
    <div className="absolute -right-20 top-20 h-72 w-72 rounded-full bg-gradient-to-br from-blue-100/30 to-indigo-100/30 blur-3xl dark:from-blue-900/20 dark:to-indigo-900/20"></div>
    <div className="absolute -left-20 bottom-20 h-72 w-72 rounded-full bg-gradient-to-tl from-purple-100/30 to-pink-100/30 blur-3xl dark:from-purple-900/20 dark:to-pink-900/20"></div>

    {/* Dark mode accent elements - only visible in dark mode */}
    <div className="absolute left-1/4 top-1/3 hidden h-32 w-32 rounded-full bg-indigo-800/10 blur-2xl dark:block"></div>
    <div className="absolute bottom-1/4 right-1/4 hidden h-24 w-24 rounded-full bg-blue-800/10 blur-2xl dark:block"></div>

    {/* Glass dividers - only in dark mode */}
    <div className="absolute hidden h-[1px] w-full max-w-[80%] bg-gradient-to-r from-transparent via-gray-800/50 to-transparent dark:block"></div>

    {/* Keyframe animation for pattern movement */}
    <style>{`
      @keyframes patternMove {
        0% { background-position: 0 0; }
        100% { background-position: 40px 40px; }
      }
    `}</style>
  </>
));
DecorativePatterns.displayName = 'DecorativePatterns';

// Memoize the Quranic verse component for better performance
const QuranicVerse = memo(() => {
  // Pre-computed verses array outside the component to avoid recreation on each render
  const verses = [
    {
      text: '﴿ إِنَّ اللَّهَ يَأْمُرُكُمْ أَن تُؤَدُّوا الْأَمَانَاتِ إِلَىٰ أَهْلِهَا ﴾',
      ref: 'سورة النساء - الآية ٥٨',
    },
    {
      text: '﴿ يَا أَيُّهَا الَّذِينَ آمَنُوا لَا تَأْكُلُوا أَمْوَالَكُم بَيْنَكُم بِالْبَاطِلِ ﴾',
      ref: 'سورة النساء - الآية ٢٩',
    },
    {
      text: '﴿ وَالَّذِينَ هُمْ لِأَمَانَاتِهِمْ وَعَهْدِهِمْ رَاعُونَ ﴾',
      ref: 'سورة المؤمنون - الآية ٨',
    },
    {
      text: '﴿ إِنَّمَا يَتَقَبَّلُ اللَّهُ مِنَ الْمُتَّقِينَ ﴾',
      ref: 'سورة المائدة - الآية ٢٧',
    },
    {
      text: '﴿ وَمَن يَغْلُلْ يَأْتِ بِمَا غَلَّ يَوْمَ الْقِيَامَةِ ﴾',
      ref: 'سورة آل عمران - الآية ١٦١',
    },
    {
      text: '﴿ وَأَوْفُوا بِالْعَهْدِ إِنَّ الْعَهْدَ كَانَ مَسْئُولًا ﴾',
      ref: 'سورة الإسراء - الآية ٣٤',
    },
    {
      text: '﴿ وَالَّذِينَ هُمْ لِأَمَانَاتِهِمْ وَعَهْدِهِمْ رَاعُونَ ﴾',
      ref: 'سورة المعارج - الآية ٣٢',
    },
  ];

  // Compute random verse index once on component mount
  const randomVerseIndex = useRef(
    Math.floor(Math.random() * verses.length),
  ).current;
  const randomVerse = verses[randomVerseIndex];

  return (
    <div className="group relative mb-3 w-full overflow-hidden rounded-lg bg-gradient-to-br from-amber-500/10 via-red-500/10 to-amber-500/10 p-2.5 shadow-lg backdrop-blur-sm transition-all duration-500 hover:shadow-red-500/10 dark:from-amber-500/20 dark:via-red-500/20 dark:to-amber-500/20">
      {/* Advanced Islamic pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.1] dark:opacity-[0.2]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
          backgroundSize: '20px 20px',
          animation: 'patternMove 15s linear infinite',
        }}
      />

      {/* Sophisticated decorative elements */}
      <div className="absolute -right-4 -top-4 h-16 w-16 rounded-full bg-gradient-to-br from-amber-500/20 to-transparent blur-xl transition-all duration-500 group-hover:scale-110"></div>
      <div className="absolute -bottom-4 -left-4 h-16 w-16 rounded-full bg-gradient-to-tl from-red-500/20 to-transparent blur-xl transition-all duration-500 group-hover:scale-110"></div>

      {/* Exquisite verse container */}
      <div className="hover:bg-white/98 dark:hover:bg-gray-900/98 relative flex flex-col items-center justify-center gap-1 rounded-md bg-white/95 p-2.5 shadow-inner backdrop-blur-sm transition-all duration-500 dark:bg-gray-900/95">
        {/* Premium decorative border */}
        <div className="absolute inset-0 rounded-md border border-amber-500/20 transition-all duration-500 group-hover:border-red-500/30"></div>

        {/* Elegant Islamic decorative elements */}
        <div className="absolute -left-1 top-1/2 h-10 w-0.5 -translate-y-1/2 rounded-full bg-gradient-to-b from-amber-500/30 to-transparent transition-all duration-500 group-hover:scale-y-110"></div>
        <div className="absolute -right-1 top-1/2 h-10 w-0.5 -translate-y-1/2 rounded-full bg-gradient-to-t from-red-500/30 to-transparent transition-all duration-500 group-hover:scale-y-110"></div>

        {/* Premium verse text */}
        <div className="relative">
          <div className="absolute -left-1.5 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-gradient-to-br from-amber-500/50 to-red-500/50 transition-all duration-500 group-hover:scale-110"></div>
          <div className="text-center text-base font-medium leading-relaxed text-gray-800 transition-all duration-500 group-hover:text-gray-900 dark:text-gray-200 dark:group-hover:text-white">
            {randomVerse.text}
            <div className="mt-0.5 flex items-center justify-center gap-1.5">
              <div className="h-px w-6 bg-gradient-to-r from-transparent via-amber-500/40 to-transparent transition-all duration-500 group-hover:via-red-500/50"></div>
              <span className="text-[10px] font-medium text-amber-600 transition-all duration-500 group-hover:text-red-600 dark:text-amber-400 dark:group-hover:text-red-400">
                {randomVerse.ref}
              </span>
              <div className="h-px w-6 bg-gradient-to-l from-transparent via-amber-500/40 to-transparent transition-all duration-500 group-hover:via-red-500/50"></div>
            </div>
          </div>
          <div className="absolute -right-1.5 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-gradient-to-bl from-amber-500/50 to-red-500/50 transition-all duration-500 group-hover:scale-110"></div>
        </div>
      </div>
    </div>
  );
});
QuranicVerse.displayName = 'QuranicVerse';

// Memoize the image section for better performance
const ImageSection = memo(() => (
  <div className="flex w-full items-center gap-6">
    {/* Image container */}
    <div className="group relative flex-shrink-0 overflow-hidden rounded-md border border-gray-100 bg-white p-4 shadow-sm transition-all duration-300 hover:shadow-md dark:border-gray-800/60 dark:bg-black/70 dark:shadow-lg">
      {/* Accent border glow - only in dark mode */}
      <div className="absolute inset-0 hidden rounded-full opacity-0 shadow-[0_0_15px_rgba(79,70,229,0.2)] transition-opacity duration-500 group-hover:opacity-100 dark:block"></div>

      <Image
        alt="chart"
        src="/images/svg/ast.svg"
        isBlurred
        className="h-[100px] w-[100px] rounded-full transition-all duration-300"
      />
    </div>

    {/* Text content */}
    <div className="flex flex-col space-y-3">
      <p className="text-sm text-gray-700 dark:text-gray-300">
        🛒 برجاء اختيار عرض شراء لتجهيز الطلبية من القائمة
      </p>

      <p className="text-xs font-medium text-red-600 dark:text-red-400">
        📊 المعدلات تحسب بيع 3 اشهر مق سومة علي 15 يوم
      </p>
    </div>
  </div>
));
ImageSection.displayName = 'ImageSection';

// Loading component
const LoadingState = memo(() => (
  <div className="flex h-[calc(100vh-230px)] flex-col items-center justify-center space-y-4">
    <div className="h-12 w-12 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600 dark:border-indigo-800 dark:border-t-indigo-400"></div>
    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
      جاري تحميل العروض...
    </p>
  </div>
));
LoadingState.displayName = 'LoadingState';

// Empty state component
const EmptyState = memo(() => (
  <div className="flex h-[calc(100vh-230px)] flex-col items-center justify-center">
    <div className="grid place-content-center rounded">
      <Image
        alt="no data"
        src="/images/svg/question.svg"
        isBlurred
        className="w-[150px]"
      />
    </div>
    <h3 className="mt-3 font-medium text-gray-700 dark:text-gray-300">
      لا يوجد عروض شراء لهذا اليوم
    </h3>
  </div>
));
EmptyState.displayName = 'EmptyState';

// Main component with performance optimizations
export const InitialStage = memo(({ OneOfferSelectForPrepareFn }: Props) => {
  const queryClient = useQueryClient();

  // Optimized query with prefetching and caching
  const allOffersRequests = useQuery({
    queryKey: ['Offers-For-Request'],
    queryFn: GetTodayOffersForRequestApi,
    staleTime: 0, // Always refetch when invalidated
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });

  const [mounted, setMounted] = useState(false);

  // Prefetch data on mount and refetch when needed
  useEffect(() => {
    const prefetchData = async () => {
      await queryClient.prefetchQuery({
        queryKey: ['Offers-For-Request'],
        queryFn: GetTodayOffersForRequestApi,
      });
    };

    prefetchData();
    setMounted(true);

    // Refetch data when component mounts to ensure fresh data
    setTimeout(() => {
      allOffersRequests.refetch();
    }, 100);

    return () => setMounted(false);
  }, [queryClient, allOffersRequests]);

  // Memoize columns definition to prevent unnecessary re-renders
  const columns = useMemo<ColumnDef<ITodayOffersForRequest>[]>(
    () => [
      createIDColumn<ITodayOffersForRequest>(),
      {
        accessorKey: 'IsRequested',
        header: '--',
        cell: ({ row }) => {
          return (
            <div className="text-large flex w-full items-center justify-center font-beiruti">
              {row?.original?.IsRequested ? (
                <BadgeCheck className="h-8 w-8 rounded bg-green-200 p-1 text-green-800" />
              ) : (
                <CircleX className="h-8 w-8 rounded bg-red-200 p-1 text-red-800" />
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'UploadUser.empName',
        header: 'موظف',
        cell: ({ row }) => {
          return (
            <div className="my-1 flex w-12 justify-center">
              <AvatarToolTip
                size={30}
                variant="rounded"
                avatar={row?.original?.UploadUser?.avatar}
                empName={row?.original?.UploadUser?.empName}
                id={row?.original?.UploadUser?._id}
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'supplierClass',
        header: 'الفئة',
        cell: ({ row }) => {
          const clientClass = row?.original?.ClientClass;
          return <ClientClassTypography clientClass={clientClass} />;
        },
      },
      {
        accessorKey: 'ClientName',
        header: ' المورد',
        cell: ({ row }) => {
          const clientID = row?.original?.ClientID;
          const clientName = row?.original?.ClientName;
          return <ClientCol clientID={clientID} clientName={clientName} />;
        },
      },
      {
        accessorKey: 'ItemsNumber',
        header: 'الاصناف',
        cell: ({ row }) => {
          return <StyledCellColumn value={row?.original?.ItemsNumber} />;
        },
      },
      {
        accessorKey: 'UploadDate',
        header: 'التاريخ',
        cell: ({ row }) => {
          return (
            <div className="flex flex-col items-center justify-center gap-0.5 dark:contrast-75">
              <h3 className="line-clamp-1 w-16 rounded bg-green-100 text-center font-sans text-xs text-green-800">
                {dayjs(new Date(row?.original?.UploadDate)).format(
                  'DD/MM/YYYY',
                )}
              </h3>
              <h3 className="line-clamp-1 w-16 rounded bg-red-100 text-center font-sans text-xs text-red-700">
                {dayjs.utc(row?.original?.UploadDate).format('hh:mm A')}
              </h3>
            </div>
          );
        },
      },
      {
        accessorKey: 'OID',
        header: '-',
        size: 30,
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <ActionCol
              row={row?.original}
              OneOfferSelectForPrepareFn={OneOfferSelectForPrepareFn}
            />
          );
        },
      },
    ],
    [OneOfferSelectForPrepareFn],
  );

  // Memoize current date
  const currentDate = useMemo(() => dayjs().format('YYYY-MM-DD'), []);

  // Memoize data for the table to prevent unnecessary re-renders
  const tableData = useMemo(() => {
    return allOffersRequests.data?.offers || [];
  }, [allOffersRequests.data?.offers]);

  return (
    <div
      className={cn(
        'relative flex h-full w-full flex-col items-center overflow-hidden bg-fixed transition-colors duration-300',
        'bg-gradient-to-br from-white via-slate-50/30 to-white dark:from-[#050505] dark:via-black dark:to-[#050505]',
      )}
    >
      <DecorativePatterns />

      {/* Main content - Using CSS Grid for layout */}
      <div
        className={cn(
          'z-10 mt-5 w-full max-w-7xl px-4 transition-all duration-300',
          mounted ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
        )}
      >
        <div className="grid h-[calc(100vh-230px)] grid-cols-1 items-center gap-6 md:grid-cols-5">
          {/* Left side - Card - 2/5 of width */}
          <div className="relative col-span-1 overflow-hidden rounded-xl bg-white/80 p-[1px] shadow-xl backdrop-blur-md dark:bg-[#0A0A0A]/80 dark:backdrop-blur-xl md:col-span-2">
            {/* Professional border gradient */}
            <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-70 dark:opacity-50"></div>

            {/* Inner content */}
            <div className="relative flex flex-col items-center justify-center rounded-xl bg-white/95 p-6 dark:bg-[#0A0A0A]/95">
              <QuranicVerse />
              {/* Main heading */}
              <div className="relative mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  ✨ طلبية شراء جديدة باستخدام العروض
                </h3>
                <div className="mx-auto mt-1 h-1 w-16 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600"></div>
              </div>

              <ImageSection />
            </div>
          </div>

          {/* Right side - Table - 3/5 of width */}
          <div className="col-span-1 flex h-full flex-col rounded-xl border border-gray-100 bg-white/90 p-4 shadow-xl backdrop-blur-md dark:border-gray-800/30 dark:bg-[#0A0A0A]/90 dark:shadow-2xl md:col-span-3">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-100 p-2 shadow-sm dark:bg-indigo-900/30">
                <NotebookPen className="h-6 w-6 text-indigo-700 dark:text-indigo-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  عروض الشراء لليوم
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  <span className="ml-1 rounded-md border border-gray-200 px-2 py-0.5 dark:border-gray-700">
                    {currentDate}
                  </span>
                  عرض جميع العروض لليوم والتي تم تحميلها من قبل الموظفين
                </p>
              </div>
            </div>

            {allOffersRequests.isLoading ? (
              <LoadingState />
            ) : tableData.length > 0 ? (
              <div className="h-[80vh]">
                <DataTable
                  columns={columns}
                  data={tableData}
                  isPaginated
                  isTopToolbar
                  isRowSelection
                  heightTable="70vh"
                />
              </div>
            ) : (
              <EmptyState />
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

InitialStage.displayName = 'InitialStage';
